import React, { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import imgLogoJamespotWhite from '../../assets/jamespot-2025-216.png';
import imgLogoJamespotBlue from '../../assets/jamespot-blue-2025-216.png';
import { JRCStyledHref } from '../JRCHref/JRCHref';
import { getContrastYIQ } from '../../utils/utils.color';

type Platform = {
    name: string;
    logo: string | null;
};

export type JRCPageNotLoggedProps = {
    platform?: Platform;
    backgroundColor?: string;
};

const Container = styled.div<{ backgroundColor?: string; backgroundImage?: string }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    max-height: 100vh;
`;
const Header = styled.header`
    display: flex;
    flex-direction: column;
    padding: 0 24px;
    background: ${(props) => props.theme.color.headerColor};
    height: var(--header-height);

    @media (min-width: 768px) {
        flex-direction: row;
        justify-content: space-between;
    }
`;
const LogoContainer = styled.div`
    margin-right: 24px;
    display: inline-flex;
    max-width: 460px;
    height: var(--header-height-line);
`;
const LogoLink = styled(JRCStyledHref)`
    display: inline-flex;
    color: ${(props) => props.theme.color.headerTextColor};
    font-size: 30px;
    align-items: center;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;
const LogoImg = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: left center;
`;
const PoweredBy = styled.div`
    display: flex;
    justify-content: end;
    align-items: center;
    gap: 8px;
`;
const PoweredByRow = styled.div`
    display: flex;
    color: ${(props) => props.theme.color.headerTextColor};
    gap: 8px;
`;
const LogoJamespot = styled.img`
    max-width: 108px;
    object-fit: contain;
    object-position: left center;
`;
const PoweredByLink = styled.a`
    padding: 8px 16px;
    border-radius: 8px;
    border: 2px solid ${(props) => props.theme.color.headerTextColor};
    color: ${(props) => props.theme.color.headerTextColor};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;
const Main = styled.div<{ backgroundColor?: string }>`
    --body-height: calc(100vh - var(--header-height));
    display: flex;
    flex: 1;
    overflow: auto;
    background-color: ${({ backgroundColor, theme }) => backgroundColor || theme.color.white};
`;

const Logo: FC<{ platform?: Platform }> = ({ platform }) =>
    platform ? (
        <LogoContainer>
            <LogoLink href="/">{platform.logo ? <LogoImg src={platform.logo} /> : platform.name}</LogoLink>
        </LogoContainer>
    ) : null;

export const JRCPageNotLogged: FC<JRCPageNotLoggedProps> = ({ children, platform, backgroundColor }) => {
    const intl = useIntl();
    const theme = useTheme();
    const poweredByLinkHref = intl.formatMessage({ id: 'POWERED_BY_Link' }) + '?from=platform';

    return (
        <Container>
            <Header>
                <Logo platform={platform} />
                <PoweredBy>
                    <PoweredByRow>
                        {intl.formatMessage({ id: 'POWERED_BY' })}
                        <LogoJamespot
                            src={
                                getContrastYIQ(theme.color.headerColor) === 'white'
                                    ? imgLogoJamespotWhite
                                    : imgLogoJamespotBlue
                            }
                            alt="Jamespot"
                        />
                    </PoweredByRow>
                    <PoweredByLink href={poweredByLinkHref}>
                        {intl.formatMessage({ id: 'POWERED_BY_See_More' })}
                    </PoweredByLink>
                </PoweredBy>
            </Header>
            <Main backgroundColor={backgroundColor}>{children}</Main>
        </Container>
    );
};
